import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface WalletAccount {
  address: string;
  chainId: number;
  isConnected: boolean;
  connector?: string;
}

export interface Transaction {
  id: string;
  hash?: string;
  type: 'encrypt' | 'decrypt' | 'hash' | 'erc' | 'other';
  status: 'pending' | 'success' | 'failed';
  timestamp: number;
  data?: unknown;
  error?: string;
}

interface WalletState {
  // Account management
  account: WalletAccount | null;
  setAccount: (account: WalletAccount | null) => void;
  
  // Connection state
  isConnecting: boolean;
  setConnecting: (connecting: boolean) => void;
  
  // Transaction management
  transactions: Transaction[];
  addTransaction: (transaction: Omit<Transaction, 'id' | 'timestamp'>) => void;
  updateTransaction: (id: string, updates: Partial<Transaction>) => void;
  removeTransaction: (id: string) => void;
  clearTransactions: () => void;
  
  // Network management
  supportedChains: number[];
  setSupportedChains: (chains: number[]) => void;
  
  // Balance tracking
  balance: string;
  setBalance: (balance: string) => void;
  
  // Connection history
  recentConnections: string[];
  addRecentConnection: (connector: string) => void;
  
  // Wallet preferences
  autoConnect: boolean;
  setAutoConnect: (autoConnect: boolean) => void;
}

export const useWalletStore = create<WalletState>()(
  persist(
    (set) => ({
      // Account state
      account: null,
      setAccount: (account) => set({ account }),
      
      // Connection state
      isConnecting: false,
      setConnecting: (connecting) => set({ isConnecting: connecting }),
      
      // Transaction state
      transactions: [],
      addTransaction: (transaction) => {
        const id = Math.random().toString(36).substr(2, 9);
        const newTransaction = {
          ...transaction,
          id,
          timestamp: Date.now()
        };
        set((state) => ({
          transactions: [newTransaction, ...state.transactions].slice(0, 50) // Keep last 50
        }));
      },
      updateTransaction: (id, updates) => set((state) => ({
        transactions: state.transactions.map(tx => 
          tx.id === id ? { ...tx, ...updates } : tx
        )
      })),
      removeTransaction: (id) => set((state) => ({
        transactions: state.transactions.filter(tx => tx.id !== id)
      })),
      clearTransactions: () => set({ transactions: [] }),
      
      // Network state
      supportedChains: [1, 5, 11155111], // Mainnet, Goerli, Sepolia
      setSupportedChains: (chains) => set({ supportedChains: chains }),
      
      // Balance state
      balance: '0',
      setBalance: (balance) => set({ balance }),
      
      // Connection history
      recentConnections: [],
      addRecentConnection: (connector) => set((state) => {
        const recent = state.recentConnections.filter(c => c !== connector);
        return {
          recentConnections: [connector, ...recent].slice(0, 5) // Keep last 5
        };
      }),
      
      // Preferences
      autoConnect: true,
      setAutoConnect: (autoConnect) => set({ autoConnect }),
    }),
    {
      name: 'wallet-store',
      partialize: (state: WalletState) => ({
        recentConnections: state.recentConnections,
        autoConnect: state.autoConnect,
        supportedChains: state.supportedChains,
      }),
    }
  )
);